import { useState, useRef, useEffect, FormEvent, useCallback } from 'react';
import { PageHeader } from '@components/PageHeader';
import { Card } from '@components/ui/Card';
import { Button } from '@components/ui/Button';
import { Spinner } from '@components/ui/Spinner';
import { sendChatMessage, ChatApiError, ChatSource } from '../../services/chatApi';
import { PaperAirplaneIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
}

export const AskLotti = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(async (e?: FormEvent) => {
    e?.preventDefault();

    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    setError(null);
    setInput('');
    setIsLoading(true);

    // Add user message immediately
    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const response = await sendChatMessage(trimmed, conversationId);

      // Store conversationId for follow-ups
      if (response.conversationId) {
        setConversationId(response.conversationId);
      }

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.answer || 'Still thinking... Please try again.',
        sources: response.sources?.length ? response.sources : undefined,
      };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      if (err instanceof ChatApiError) {
        if (err.status === 429) {
          setError('Service is busy. Please try again in a moment.');
        } else if (err.status === 401) {
          setError('Session expired. Please sign in again.');
        } else {
          setError(err.message);
        }
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [input, isLoading, conversationId]);

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-[calc(100vh-5rem)] flex-col md:h-[calc(100vh-7rem)]">
      <PageHeader
        title="Ask Lotti"
        subtitle="Your BlocPlus AI assistant"
        actions={
          messages.length > 0 ? (
            <Button variant="outlined" onClick={handleNewConversation}>
              <ArrowPathIcon className="h-4 w-4" />
              New Conversation
            </Button>
          ) : undefined
        }
      />

      <Card className="flex flex-1 flex-col overflow-hidden">
        {/* Messages area */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 && !isLoading && (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gold-light">
                  <span className="text-3xl">✨</span>
                </div>
                <h3 className="mb-2 text-l font-bold text-gray-800">Hi, I'm Lotti!</h3>
                <p className="mx-auto max-w-sm text-s text-gray-600">
                  I'm your AI assistant for all things BlocPlus. Ask me about
                  features, workflows, or anything related to the BlocPlus platform.
                </p>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-3 md:max-w-[75%] ${
                  msg.role === 'user'
                    ? 'bg-gold text-white'
                    : 'border border-gray-200 bg-gray-50 text-gray-800'
                }`}
              >
                <p className="whitespace-pre-wrap text-s">{msg.content}</p>

                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-2 border-t border-gray-200 pt-2">
                    <p className="mb-1 text-xs font-bold text-gray-500">Sources:</p>
                    {msg.sources.map((source, si) => (
                      <p key={si} className="text-xs text-gray-500">
                        {source.reference ? (
                          <a
                            href={source.reference}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-gray-700"
                          >
                            {source.title}
                          </a>
                        ) : (
                          source.title
                        )}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                <Spinner size="sm" />
                <span className="text-s text-gray-500">Lotti is thinking...</span>
              </div>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3">
              <p className="text-s text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="mt-1 text-xs font-bold text-red-600 underline hover:text-red-800"
              >
                Dismiss
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="border-t border-gray-200 p-4">
          <form onSubmit={handleSend} className="flex items-end gap-3">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask Lotti a question..."
              disabled={isLoading}
              rows={1}
              className="input-field max-h-32 min-h-[2.5rem] flex-1 resize-none disabled:opacity-50"
              style={{ fieldSizing: 'content' } as React.CSSProperties}
            />
            <Button
              type="submit"
              disabled={isLoading || !input.trim()}
              className="shrink-0"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
            </Button>
          </form>
          <p className="mt-2 text-xs text-gray-400">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </Card>
    </div>
  );
};
